import { useNavigateWithSearchParams } from "@p0security/frontend-core";
import { itemRouteSegment } from "@p0security/shared/install/routes";
import type {
  ConfigOf,
  Element,
  InstallSpec,
  ItemComponent,
  Option,
} from "@p0security/shared/install/types";
import { Alert, Spacing, Typography } from "@p0security/ui";
import { omit, upperFirst } from "lodash";
import { type ReactNode, useCallback, useMemo } from "react";
import { useLocation } from "react-router";

import type {
  FrontendComponentInstaller,
  FrontendInstallContext,
  FrontendInstaller,
} from "../types";
import type { ComponentProps } from "./Component";
import { NEW_SENTINEL } from "./Component";
import { configPath } from "./Config";
import { ConfigForm } from "./ConfigForm";
import { evaluate } from "./util.ts";

export type NewProps<T extends InstallSpec> = ComponentProps<T> & {
  component: ItemComponent<any>;
  context: FrontendInstallContext<ConfigOf<T>>;
};

/** Router state that pre-fills the form when returning from a staged item */
export type NewLocationState = {
  stagedId: string;
};

/** Recursively extracts "new"-step fields from a component schema */
const pickNew = <S extends Record<string, Element>>(
  schema: S
): Record<string, Element> => {
  const output: Record<string, Element> = {};
  for (const [key, item] of Object.entries(schema)) {
    if (item.step !== "new") continue;
    if (item.type !== "select") {
      output[key] = item;
    } else {
      const options: Record<string, Option<any>> = {};
      for (const [k, option] of Object.entries(item.options)) {
        options[k] = { ...option, schema: pickNew(option.schema) };
      }
      output[key] = { ...item, options };
    }
  }
  return output;
};

/** Recursively picks an item's values for the fields of a "new"-step schema
 *
 * Generated and hidden fields are skipped: the user never enters them, and the
 * backend may derive them from the item id, so an item submitted under a
 * different id must not carry the old value.
 */
const pickNewValues = (
  schema: Record<string, Element>,
  value: Record<string, any>
): Record<string, any> => {
  const output: Record<string, any> = {};
  for (const [key, element] of Object.entries(schema)) {
    const field = value[key];
    if (
      field === undefined ||
      element.type === "generated" ||
      element.type === "hidden"
    )
      continue;
    if (element.type === "select") {
      if (field?.type in element.options) {
        output[key] = {
          ...pickNewValues(element.options[field.type].schema, field),
          type: field.type,
        };
      }
    } else {
      output[key] = field;
    }
  }
  return output;
};

export const New = <T extends InstallSpec>(props: NewProps<T>) => {
  const {
    authFetch,
    component,
    componentKey,
    config,
    context,
    installer,

    registration,
  } = props;
  const navigate = useNavigateWithSearchParams();
  const identifier = useMemo(
    () =>
      registration.identifier
        ? evaluate(registration.identifier, componentKey)
        : "identifier",
    [componentKey, registration.identifier]
  );
  const itemKind = useMemo(
    () => evaluate(registration.itemKind, componentKey),
    [componentKey, registration.itemKind]
  );
  const schema = {
    id: {
      ...omit(component, "schema"),
      label: `${upperFirst(itemKind)} ${identifier}`,
      description: undefined,
      step: "new",
    },
    ...pickNew(component.schema),
  };
  const { state } = useLocation() as { state: NewLocationState | null };
  const stagedId = state?.stagedId;
  const staged = useMemo(
    () =>
      stagedId
        ? (config?.[componentKey] as Record<string, any> | undefined)?.[
            stagedId
          ]
        : undefined,
    [componentKey, config, stagedId]
  );
  const submit = useCallback(
    async (item: object & { id: string }) => {
      if (!config) {
        const createResponse = await authFetch(
          configPath({
            integration: props.integration,
            componentKey: undefined,
            id: undefined,
          }),
          // TODO: root config
          { method: "POST", json: {} }
        );
        if (!createResponse) return;
      }
      const response = await authFetch(configPath({ ...props, id: item.id }), {
        method: "PUT",
        json: omit(item, "id"),
      });
      if (response?.ok) {
        // A changed identifier created a new item, so remove the staged one
        // the user came back from. One that has moved past staging stays.
        if (stagedId && stagedId !== item.id && staged?.state === "stage") {
          await authFetch(configPath({ ...props, id: stagedId }), {
            method: "DELETE",
          });
        }
        navigate(`../${itemRouteSegment(item.id)}`, { relative: "path" });
      }
    },
    [authFetch, config, navigate, props, staged, stagedId]
  );
  const onSubmit = useCallback(
    (item: object & { id: string }) => void submit(item),
    [submit]
  );

  const idInstaller = useMemo(() => {
    const componentInstaller = (installer?.[componentKey] ??
      {}) as FrontendInstaller<any>["components"];
    return {
      items: { id: componentInstaller, ...componentInstaller.items },
    } as FrontendComponentInstaller<any>;
  }, [componentKey, installer]);

  const emptyConfig = useMemo(() => ({}), []);

  const initialConfig = useMemo(
    () =>
      staged
        ? {
            id: stagedId,
            ...pickNewValues(pickNew(component.schema), staged),
          }
        : emptyConfig,
    [component.schema, emptyConfig, staged, stagedId]
  );

  return (
    <>
      {
        // No item exists yet, so the item argument is empty.
        // TODO: Fix typing -- `Config.tsx` casts at this same seam.
        installer?.[componentKey]?.newPreamble?.(
          context,
          NEW_SENTINEL,
          emptyConfig as ConfigOf<T>[string][string]
        ) as ReactNode
      }
      <Typography.Title level={5}>Installing a new {itemKind}</Typography.Title>
      <Typography.Paragraph>
        {component.type === "string" ? "Enter" : "Select"} the {identifier} of
        the {itemKind} to install, then click &quot;Next&quot;:
      </Typography.Paragraph>
      {staged?.state === "stage" && (
        // Mirrors the note on the delete dialog, which this path skips
        <Spacing asChild mb={16}>
          <Alert
            type="warning"
            showIcon
            message={`Changing the ${identifier} removes ${stagedId} from P0. Anything you already set up for ${stagedId} in your environment stays, so remove it yourself if you no longer need it.`}
          />
        </Spacing>
      )}
      <ConfigForm
        config={initialConfig}
        context={context}
        id={NEW_SENTINEL}
        installer={idInstaller}
        isFetching={props.isFetching}
        item={initialConfig}
        schema={schema}
        setConfig={onSubmit}
        step="new"
      />
    </>
  );
};
